import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header";
import Footer from "./Footer";
import SubscriptionModal from "./SubscriptionModal";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import problemLibrary from "./data/problem_library.v1.json";

const ANALYSIS_API_BY_REGION = {
  de: (
    process.env.REACT_APP_ANALYSIS_API_URL_DE ||
    process.env.REACT_APP_ANALYSIS_API_URL ||
    "https://api.swingmentor.pro"
  ).replace(/\/+$/, ""),
  ru: (
    process.env.REACT_APP_ANALYSIS_API_URL_RU ||
    "https://api-ru.swingmentor.pro"
  ).replace(/\/+$/, ""),
};

const analysisApiForUser = (user) => {
  const explicitApi = user?.data_region_api_url || user?.api_base_url;
  if (explicitApi) return String(explicitApi).replace(/\/+$/, "");

  const region = String(user?.data_region || "").trim().toLowerCase();
  return ANALYSIS_API_BY_REGION[region] || ANALYSIS_API_BY_REGION.de;
};
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const POLL_INTERVAL_MS = 4000;
const PENDING_TASK_PREFIX = "sm_pending_analysis_task";
const asList = (value) => (Array.isArray(value) ? value : []);
const DRILL_CATALOG = Object.fromEntries(
  asList(problemLibrary.problems).flatMap((problem) =>
    asList(problem.drills).map((drill) => [
      drill.id,
      {
        ...drill,
        visual_asset: problemLibrary.drill_visuals?.[drill.id],
      },
    ]),
  ),
);
const PHASE_LABELS = {
  ru: {
    address: "Стойка",
    setup: "Стойка",
    backswing: "Замах",
    top: "Верхняя точка",
    transition: "Переход вниз",
    downswing: "Движение вниз",
    impact: "Момент удара",
    follow_through: "Финиш",
    finish: "Финиш",
  },
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || data.error || "Request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const videoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = Number(video.duration);
      URL.revokeObjectURL(url);
      Number.isFinite(duration) ? resolve(duration) : reject(new Error("Invalid duration"));
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Invalid video"));
    };
    video.src = url;
  });

const localizedValue = (item, field, lang) =>
  item?.[`${field}_${lang}`] ||
  item?.[`${field}_en`] ||
  item?.[field] ||
  "";

const drillImageUrl = (drill) => {
  const visual = drill?.visual_asset || {};
  if (visual.image_url) return visual.image_url;

  const assetId =
    visual.processed_asset_id ||
    visual.asset_id?.replace(/_v\d+$/, "_generated");
  return assetId ? `/images/drills/${assetId}.webp` : "";
};

const phaseLabel = (phase, lang) =>
  PHASE_LABELS[lang]?.[String(phase || "").toLowerCase()] ||
  phase ||
  "";

const AiMentors = () => {
  const { user, token } = useAuth();
  const { lang, t } = useLanguage();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [credits, setCredits] = useState(null);
  const [policy, setPolicy] = useState({
    creditsPerSecond: 1,
    minimumCharge: 1,
    maxDurationSeconds: 30,
  });
  const [showPaywall, setShowPaywall] = useState(false);

  const pendingTaskKey = useMemo(
    () => `${PENDING_TASK_PREFIX}:${user?.id || user?.email || "anonymous"}`,
    [user],
  );
  const analysisApi = useMemo(() => analysisApiForUser(user), [user]);
  const processing = Boolean(taskId) && taskStatus === "processing";

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const updateCredits = useCallback((value) => {
    if (value === undefined || value === null) return;
    setCredits(Number(value));
    window.dispatchEvent(new Event("sm-subscription-updated"));
  }, []);

  const loadBalance = useCallback(async () => {
    if (!token) return null;
    const response = await fetch(`${analysisApi}/api/credits/balance`, {
      headers: authHeaders(),
    });
    const data = await parseResponse(response);
    updateCredits(data.credit_balance);
    setPolicy({
      creditsPerSecond: Number(data.credits_per_second || 1),
      minimumCharge: Number(data.minimum_charge || 1),
      maxDurationSeconds: Number(data.max_duration_seconds || 30),
    });
    if (data.active_task?.task_id) {
      setTaskId(data.active_task.task_id);
      setTaskStatus("processing");
      localStorage.setItem(pendingTaskKey, data.active_task.task_id);
    }
    return data;
  }, [analysisApi, authHeaders, pendingTaskKey, token, updateCredits]);

  useEffect(() => {
    if (!token) {
      setCredits(null);
      setTaskId(null);
      setTaskStatus("idle");
      return;
    }

    const storedTask = localStorage.getItem(pendingTaskKey);
    if (storedTask) {
      setTaskId(storedTask);
      setTaskStatus("processing");
    }
    loadBalance().catch((requestError) => {
      if (requestError.status === 401) setError(t("mentors.sessionExpired"));
    });
  }, [loadBalance, pendingTaskKey, t, token]);

  useEffect(() => {
    if (!taskId || !token || taskStatus !== "processing") return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const response = await fetch(
          `${analysisApi}/upload/status/${encodeURIComponent(taskId)}`,
          { headers: authHeaders() },
        );
        const data = await parseResponse(response);
        if (cancelled) return;
        updateCredits(data.credit_balance);

        if (data.status === "done" && data.result) {
          setResult(data.result);
          updateCredits(data.result.credit_balance ?? data.credit_balance);
          setTaskStatus("done");
          setTaskId(null);
          localStorage.removeItem(pendingTaskKey);
          return;
        }
        if (data.status === "error") {
          setError(data.error || t("mentors.processingFailed"));
          setTaskStatus("error");
          setTaskId(null);
          localStorage.removeItem(pendingTaskKey);
          loadBalance().catch(() => {});
          return;
        }
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
      } catch (requestError) {
        if (cancelled) return;
        if (requestError.status === 404) {
          localStorage.removeItem(pendingTaskKey);
          setTaskId(null);
          setTaskStatus("idle");
          setError(t("mentors.taskNotFound"));
          return;
        }
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    authHeaders,
    analysisApi,
    loadBalance,
    pendingTaskKey,
    taskId,
    taskStatus,
    t,
    token,
    updateCredits,
  ]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const applyFile = async (selectedFile) => {
    if (!selectedFile) return;
    setError("");
    setResult(null);

    if (!selectedFile.type.startsWith("video/")) {
      setError(t("mentors.invalidFile"));
      return;
    }
    if (selectedFile.size > MAX_FILE_BYTES) {
      setError(t("mentors.fileTooLarge"));
      return;
    }

    try {
      const selectedDuration = await videoDuration(selectedFile);
      if (selectedDuration > policy.maxDurationSeconds) {
        setError(
          t("mentors.videoTooLong").replace(
            "{{seconds}}",
            policy.maxDurationSeconds,
          ),
        );
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setFile(selectedFile);
      setDuration(selectedDuration);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    } catch {
      setError(t("mentors.invalidFile"));
    }
  };

  const handleUpload = async () => {
    if (!file || !token || processing) return;
    setError("");
    setResult(null);

    const formData = new FormData();
    formData.append("video", file, file.name);
    formData.append("lang", lang);

    try {
      setTaskStatus("uploading");
      const response = await fetch(`${analysisApi}/upload`, {
        method: "POST",
        headers: authHeaders(),
        body: formData,
      });
      const data = await parseResponse(response);
      updateCredits(data.credit_balance);
      setTaskId(data.task_id);
      setTaskStatus("processing");
      localStorage.setItem(pendingTaskKey, data.task_id);
    } catch (requestError) {
      if (requestError.status === 402) {
        updateCredits(requestError.data?.credit_balance);
        setShowPaywall(true);
      } else if (requestError.status === 409) {
        await loadBalance().catch(() => {});
        setError(t("mentors.alreadyProcessing"));
      } else if (requestError.status === 401) {
        setError(t("mentors.sessionExpired"));
      } else {
        setError(requestError.message || t("mentors.uploadFailed"));
      }
      setTaskStatus("idle");
    }
  };

  const structured = result?.structured_analysis || {};
  const coachAdvice = result?.coach_advice || {};
  const primaryIssue = coachAdvice.primary_issue;
  const scores = structured.scores || result?.scores || {};
  const rawAnalysisErrors = asList(structured.errors);
  const strengths = asList(structured.strengths);
  const practicePlan = asList(coachAdvice.practice_plan).map((drill) => ({
    ...drill,
    ...(DRILL_CATALOG[drill.id] || {}),
    visual_asset:
      drill.visual_asset || DRILL_CATALOG[drill.id]?.visual_asset,
  }));
  const phaseImages = result?.phase_images || {};
  const databaseFindings =
    lang === "ru" && primaryIssue
      ? [
          {
            phase: primaryIssue.phase,
            issue: primaryIssue.name_ru,
            explanation: primaryIssue.explanation_ru,
            impact: primaryIssue.why_it_matters_ru,
            feelCue: primaryIssue.feel_cue_ru,
            successCheck: primaryIssue.success_check_ru,
          },
        ]
      : [];
  const analysisErrors =
    lang === "ru" ? databaseFindings : rawAnalysisErrors;
  const visibleStrengths = lang === "ru" ? [] : strengths;
  const summary =
    lang === "ru"
      ? coachAdvice.summary_ru || t("mentors.genericSummary")
      : structured.summary ||
        result?.message ||
        result?.chatgpt_response ||
        result?.chatgpt ||
        "";
  const estimatedCredits = duration
    ? Math.max(
        policy.minimumCharge,
        Math.ceil(duration * policy.creditsPerSecond),
      )
    : null;

  if (!user || !token) {
    return (
      <PageContainer>
        <Header />
        <AuthGate>
          <Eyebrow>{t("mentors.eyebrow")}</Eyebrow>
          <PageTitle>{t("mentors.authTitle")}</PageTitle>
          <PageSub>{t("mentors.authText")}</PageSub>
          <AuthActions>
            <PrimaryLink to="/log-in">{t("auth.signIn")}</PrimaryLink>
            <SecondaryLink to="/register">{t("auth.signUp")}</SecondaryLink>
          </AuthActions>
        </AuthGate>
        <Footer />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header />
      <AnimatePresence>
        {showPaywall && (
          <SubscriptionModal onClose={() => setShowPaywall(false)} />
        )}
      </AnimatePresence>

      <PageInner>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Eyebrow>{t("mentors.eyebrow")}</Eyebrow>
          <PageTitle>{t("mentors.title")}</PageTitle>
          <PageSub>{t("mentors.sub")}</PageSub>
          <CreditRow>
            <CreditChip $warn={credits === 0}>
              {t("mentors.credits").replace(
                "{{count}}",
                credits === null ? "-" : credits.toLocaleString(),
              )}
            </CreditChip>
            <PolicyText>
              {t("mentors.limits")
                .replace("{{seconds}}", policy.maxDurationSeconds)
                .replace("{{megabytes}}", "100")}
            </PolicyText>
          </CreditRow>
        </motion.div>

        <FilmingGuide>
          <FilmingGuideImage
            src="/images/swing-capture-guide.jpg"
            alt={t("mentors.filmingGuideImageAlt")}
          />
          <FilmingGuideContent>
            <SectionLabel>{t("mentors.filmingGuideEyebrow")}</SectionLabel>
            <FilmingGuideTitle>{t("mentors.filmingGuideTitle")}</FilmingGuideTitle>
            <FilmingGuideList>
              <li>{t("mentors.filmingGuideAngle")}</li>
              <li>{t("mentors.filmingGuideHeight")}</li>
              <li>{t("mentors.filmingGuideFrame")}</li>
            </FilmingGuideList>
            <TrimNotice>
              <strong>{t("mentors.trimNoticeTitle")}</strong>
              <span>{t("mentors.trimNoticeText")}</span>
            </TrimNotice>
          </FilmingGuideContent>
        </FilmingGuide>

        <ContentGrid>
          <div>
            <DropZone
              $dragOver={dragOver}
              $disabled={processing}
              onClick={() => !processing && inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                if (!processing) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOver(false);
                if (!processing) applyFile(event.dataTransfer.files[0]);
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept="video/*"
                hidden
                disabled={processing}
                onChange={(event) => applyFile(event.target.files[0])}
              />
              <DropMark>{file ? "MP4" : "VIDEO"}</DropMark>
              <DropTitle>
                {file ? t("mentors.videoSelected") : t("mentors.dropTitle")}
              </DropTitle>
              <DropHint>
                {file
                  ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                  : t("mentors.dropHint")}
              </DropHint>
            </DropZone>

            {file && !processing && (
              <UploadBtn
                onClick={handleUpload}
                disabled={taskStatus === "uploading"}
              >
                {taskStatus === "uploading"
                  ? t("mentors.uploadingBtn")
                  : t("mentors.analyzeBtn")}
              </UploadBtn>
            )}

            {duration !== null && !processing && (
              <Estimate>
                {t("mentors.estimate")
                  .replace("{{seconds}}", duration.toFixed(1))
                  .replace("{{credits}}", estimatedCredits)}
              </Estimate>
            )}

            {error && <ErrorBox>{error}</ErrorBox>}

            {previewUrl && (
              <VideoSection>
                <SectionLabel>{t("mentors.yourVideo")}</SectionLabel>
                <VideoBox src={previewUrl} controls playsInline />
              </VideoSection>
            )}
          </div>

          <ResultsPanel>
            {processing ? (
              <LoadingState>
                <Spinner />
                <LoadingText>{t("mentors.loadingText")}</LoadingText>
                <LoadingHint>{t("mentors.loadingHint")}</LoadingHint>
              </LoadingState>
            ) : result ? (
              <Results>
                <ResultHeader>
                  <div>
                    <SectionLabel>{t("mentors.analysisReady")}</SectionLabel>
                    <ResultTitle>{t("mentors.coachNotes")}</ResultTitle>
                  </div>
                  {scores.overall !== undefined && (
                    <OverallScore>
                      <strong>{scores.overall}</strong>
                      <span>/ 100</span>
                    </OverallScore>
                  )}
                </ResultHeader>

                {summary && <Summary>{summary}</Summary>}

                {Object.keys(scores).length > 0 && (
                  <ScoreGrid>
                    {["posture", "timing", "rotation"].map(
                      (key) =>
                        scores[key] !== undefined && (
                          <ScoreCard key={key}>
                            <span>{t(`mentors.score_${key}`)}</span>
                            <strong>{scores[key]}</strong>
                          </ScoreCard>
                        ),
                    )}
                  </ScoreGrid>
                )}

                {analysisErrors.length > 0 && (
                  <ResultSection>
                    <ResultSectionTitle>
                      {t("mentors.improvements")}
                    </ResultSectionTitle>
                    {analysisErrors.map((item, index) => {
                      const image = phaseImages[item.phase];
                      return (
                        <Finding
                          key={`${item.phase || "finding"}-${index}`}
                          $hasImage={Boolean(image)}
                        >
                          {image && (
                            <FindingImage src={image} alt={item.phase || ""} />
                          )}
                          <div>
                            <FindingMeta>
                              {phaseLabel(item.phase, lang) || t("mentors.swing")}
                            </FindingMeta>
                            <FindingTitle>
                              {item.issue || item.title || String(item)}
                            </FindingTitle>
                            {item.explanation && (
                              <FindingText>{item.explanation}</FindingText>
                            )}
                            {item.impact && <FindingText>{item.impact}</FindingText>}
                            {item.feelCue && (
                              <FindingTip>
                                <strong>{t("mentors.feelCue")}:</strong>{" "}
                                {item.feelCue}
                              </FindingTip>
                            )}
                            {item.successCheck && (
                              <FindingTip>
                                <strong>{t("mentors.successCheck")}:</strong>{" "}
                                {item.successCheck}
                              </FindingTip>
                            )}
                          </div>
                        </Finding>
                      );
                    })}
                  </ResultSection>
                )}

                {visibleStrengths.length > 0 && (
                  <ResultSection>
                    <ResultSectionTitle>{t("mentors.strengths")}</ResultSectionTitle>
                    <PlainList>
                      {visibleStrengths.map((strength, index) => (
                        <li key={index}>
                          {typeof strength === "string"
                            ? strength
                            : strength.title || strength.issue || JSON.stringify(strength)}
                        </li>
                      ))}
                    </PlainList>
                  </ResultSection>
                )}

                {practicePlan.length > 0 && (
                  <ResultSection>
                    <ResultSectionTitle>
                      {t("mentors.practicePlan")}
                    </ResultSectionTitle>
                    <PracticeGrid>
                      {practicePlan.map((drill, index) => {
                        const name =
                          localizedValue(drill, "name", lang) ||
                          drill.title ||
                          t("mentors.drill");
                        const instructions = localizedValue(
                          drill,
                          "instructions",
                          lang,
                        );
                        const reps = localizedValue(drill, "reps", lang);
                        const success = localizedValue(drill, "success", lang);
                        const image = drillImageUrl(drill);

                        return (
                          <PracticeCard key={drill.id || index} $hasImage={Boolean(image)}>
                            {image && (
                              <PracticeImage
                                src={image}
                                alt={name}
                                loading="lazy"
                              />
                            )}
                            <PracticeContent>
                              <PracticeTitle>{name}</PracticeTitle>
                              {instructions && <PracticeText>{instructions}</PracticeText>}
                              {reps && (
                                <PracticeDetail>
                                  <strong>{t("mentors.repetitions")}:</strong> {reps}
                                </PracticeDetail>
                              )}
                              {success && (
                                <PracticeDetail>
                                  <strong>{t("mentors.successCheck")}:</strong>{" "}
                                  {success}
                                </PracticeDetail>
                              )}
                            </PracticeContent>
                          </PracticeCard>
                        );
                      })}
                    </PracticeGrid>
                  </ResultSection>
                )}

                {result.processed_video_url && (
                  <ResultSection>
                    <ResultSectionTitle>
                      {t("mentors.processedVideo")}
                    </ResultSectionTitle>
                    <VideoBox
                      src={result.processed_video_url}
                      controls
                      playsInline
                    />
                    <DownloadLink
                      href={result.processed_video_url}
                      target="_blank"
                      rel="noreferrer"
                      download
                    >
                      {t("mentors.downloadBtn")}
                    </DownloadLink>
                  </ResultSection>
                )}
              </Results>
            ) : (
              <EmptyState>
                <EmptyMark>AI</EmptyMark>
                <EmptyText>{t("mentors.emptyTitle")}</EmptyText>
                <EmptyHint>{t("mentors.emptyHint")}</EmptyHint>
              </EmptyState>
            )}
          </ResultsPanel>
        </ContentGrid>
      </PageInner>
      <Footer />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #111;
  font-family: "Montserrat", sans-serif;
`;

const PageInner = styled.main`
  flex: 1;
  padding: 8.5rem 6vw 5rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  @media (max-width: 768px) {
    padding: 7rem 1.25rem 3rem;
  }
`;

const AuthGate = styled.main`
  flex: 1;
  padding: 11rem 6vw 7rem;
  max-width: 760px;
`;

const Eyebrow = styled.div`
  margin-bottom: 0.75rem;
  color: #58ad55;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.28em;
  text-transform: uppercase;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.75rem;
  font-size: clamp(2.2rem, 5vw, 4.6rem);
  line-height: 0.98;
`;

const PageSub = styled.p`
  max-width: 680px;
  margin: 0;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.65;
`;

const AuthActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const PrimaryLink = styled(Link)`
  padding: 0.85rem 1.35rem;
  border-radius: 5px;
  background: #64b35d;
  color: #081108;
  font-weight: 800;
  text-decoration: none;
`;

const SecondaryLink = styled(PrimaryLink)`
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: transparent;
`;

const CreditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const CreditChip = styled.div`
  padding: 0.42rem 0.85rem;
  border: 1px solid ${({ $warn }) => ($warn ? "#eaa" : "#b9dcb7")};
  border-radius: 999px;
  color: ${({ $warn }) => ($warn ? "#b3261e" : "#337b31")};
  background: ${({ $warn }) => ($warn ? "#fff3f2" : "#f1f9f0")};
  font-size: 0.78rem;
  font-weight: 800;
`;

const FilmingGuide = styled.section`
  display: grid;
  grid-template-columns: minmax(180px, 260px) 1fr;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #f8faf8;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FilmingGuideImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 310px;
  object-fit: cover;
  object-position: center 56%;

  @media (max-width: 720px) {
    height: 340px;
    min-height: 0;
    object-position: center 58%;
  }
`;

const FilmingGuideContent = styled.div`
  padding: clamp(1.35rem, 3vw, 2.25rem);
`;

const FilmingGuideTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.35rem, 2.8vw, 2rem);
`;

const FilmingGuideList = styled.ul`
  display: grid;
  gap: 0.7rem;
  margin: 1.25rem 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding-left: 1.4rem;
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.86rem;
    line-height: 1.55;
  }

  li::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #4b9647;
    font-weight: 900;
  }
`;

const TrimNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.9rem 1rem;
  border: 1px solid #d9b96a;
  border-radius: 8px;
  background: #fff9e9;
  color: #6f5412;
  font-size: 0.8rem;
  line-height: 1.5;

  strong {
    color: #4d3908;
    font-size: 0.86rem;
  }
`;

const PolicyText = styled.span`
  color: rgba(0, 0, 0, 0.42);
  font-size: 0.78rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
  gap: 2.2rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DropZone = styled.div`
  min-height: 220px;
  padding: 2.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  border: 2px dashed
    ${({ $dragOver }) => ($dragOver ? "#58ad55" : "rgba(0, 0, 0, 0.15)")};
  border-radius: 12px;
  background: ${({ $dragOver }) => ($dragOver ? "#f0f8ef" : "#f8faf8")};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  text-align: center;
`;

const DropMark = styled.div`
  min-width: 58px;
  padding: 0.5rem;
  border-radius: 6px;
  background: #111;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
`;

const DropTitle = styled.div`
  max-width: 100%;
  font-weight: 750;
  word-break: break-word;
`;

const DropHint = styled.div`
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.8rem;
`;

const UploadBtn = styled.button`
  width: 100%;
  margin-top: 0.9rem;
  padding: 0.95rem 1.25rem;
  border: 0;
  border-radius: 6px;
  background: #64b35d;
  color: #081108;
  cursor: pointer;
  font: inherit;
  font-weight: 800;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

const Estimate = styled.div`
  margin-top: 0.7rem;
  color: rgba(0, 0, 0, 0.48);
  font-size: 0.78rem;
`;

const ErrorBox = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #efb2ae;
  border-radius: 6px;
  background: #fff4f3;
  color: #9b211b;
  font-size: 0.85rem;
`;

const VideoSection = styled.div`
  margin-top: 1.5rem;
`;

const SectionLabel = styled.div`
  margin-bottom: 0.55rem;
  color: rgba(0, 0, 0, 0.38);
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const VideoBox = styled.video`
  width: 100%;
  max-height: 520px;
  display: block;
  border-radius: 10px;
  background: #101010;
`;

const ResultsPanel = styled.section`
  min-height: 420px;
  padding: clamp(1.25rem, 3vw, 2rem);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #f8faf8;
`;

const LoadingState = styled.div`
  min-height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  text-align: center;
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border: 3px solid rgba(0, 0, 0, 0.08);
  border-top-color: #58ad55;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-weight: 800;
`;

const LoadingHint = styled.div`
  max-width: 380px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 0.82rem;
  line-height: 1.5;
`;

const EmptyState = styled(LoadingState)``;

const EmptyMark = styled.div`
  width: 54px;
  height: 54px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e6f3e4;
  color: #3c8738;
  font-weight: 900;
`;

const EmptyText = styled.div`
  font-weight: 800;
`;

const EmptyHint = styled(LoadingHint)``;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
`;

const ResultTitle = styled.h2`
  margin: 0;
  font-size: 1.45rem;
`;

const OverallScore = styled.div`
  min-width: 76px;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: #111;
  color: #fff;
  text-align: center;

  strong {
    display: block;
    font-size: 1.45rem;
  }

  span {
    font-size: 0.65rem;
    opacity: 0.58;
  }
`;

const Summary = styled.p`
  margin: 0;
  padding: 1rem 1.1rem;
  border-left: 3px solid #64b35d;
  background: #fff;
  color: rgba(0, 0, 0, 0.72);
  line-height: 1.65;
  white-space: pre-wrap;
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.65rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ScoreCard = styled.div`
  padding: 0.85rem;
  border-radius: 7px;
  background: #fff;
  text-align: center;

  span {
    display: block;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.7rem;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 0.3rem;
    font-size: 1.25rem;
  }
`;

const ResultSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ResultSectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
`;

const Finding = styled.article`
  display: grid;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? "112px 1fr" : "1fr")};
  gap: 0.85rem;
  padding: 0.85rem;
  border-radius: 8px;
  background: #fff;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FindingImage = styled.img`
  width: 112px;
  height: 84px;
  border-radius: 6px;
  object-fit: cover;

  @media (max-width: 520px) {
    width: 100%;
    height: auto;
  }
`;

const FindingMeta = styled.div`
  color: #4b9647;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const FindingTitle = styled.div`
  margin-top: 0.2rem;
  font-weight: 800;
`;

const FindingText = styled.p`
  margin: 0.4rem 0 0;
  color: rgba(0, 0, 0, 0.58);
  font-size: 0.82rem;
  line-height: 1.5;
`;

const FindingTip = styled(FindingText)`
  color: #2d6d2a;
`;

const PlainList = styled.ul`
  margin: 0;
  padding-left: 1.2rem;
  color: rgba(0, 0, 0, 0.68);
  line-height: 1.65;
`;

const PracticeGrid = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const PracticeCard = styled.article`
  display: grid;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? "180px 1fr" : "1fr")};
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #fff;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const PracticeImage = styled.img`
  width: 180px;
  height: 180px;
  border-radius: 8px;
  object-fit: cover;

  @media (max-width: 620px) {
    width: 100%;
    height: auto;
    max-height: 360px;
  }
`;

const PracticeContent = styled.div`
  min-width: 0;
`;

const PracticeTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
`;

const PracticeText = styled.p`
  margin: 0.55rem 0 0;
  color: rgba(0, 0, 0, 0.64);
  font-size: 0.82rem;
  line-height: 1.55;
`;

const PracticeDetail = styled.p`
  margin: 0.55rem 0 0;
  color: #397c36;
  font-size: 0.78rem;
  line-height: 1.5;

  strong {
    font-weight: 800;
  }
`;

const DownloadLink = styled.a`
  align-self: flex-start;
  color: #3d8939;
  font-size: 0.82rem;
  font-weight: 800;
`;

export default AiMentors;
